import { Trans, useLingui } from "@lingui/react/macro";
import NoResults from "@/components/NoResults/NoResults";
import styles from "./ShiftWithRoutesAndActivities.module.scss";
import enlargedDropZone from "../DragAndDrop/enlargedDropZone.module.scss";
import clsx from "clsx";
import { Heading } from "@medoma/components/Heading/Heading";
import {
  medicalCompetenceDictionary,
  shiftName,
  shiftTypesDictionary,
  unusualShiftTypes,
} from "@medoma/models/shifts";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { ShiftTime } from "@/components/Time/ShiftTime";
import type { IScheduledShift } from "@medoma/models/shifts";
import {
  getPatientNameWithStatus,
  useAreas,
  useTeams,
  type IListPatient,
} from "@/api/Patients";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import {
  categorySchema,
  getActivityOccurrenceStatusTimestamp,
  getGroupStatusTimestamp,
  isGroup,
  timeOfDayDictionary,
  timeOfDaySchema,
  visitStatusSchema,
  type INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
} from "@medoma/models/activities";
import {
  calculateVisitTimes,
  getVisitStatusTimestamp,
  isVisitOutsideShiftSpan,
  type IVisit,
} from "@medoma/models/visits";
import { createContext, useContext } from "react";
import { useSelectionInTimeline } from "../Selection/useSelectionInTimeline";
import { Text } from "@medoma/components/Text/Text";
import DurationChip from "@/components/Chips/DurationChip";
import { calculateGroupDuration } from "@/components/Chips/calculateGroupDuration";
import { activityCategoryDictionary } from "@/api/Activities";
import {
  activityRequirementSchema,
  aggregateRequiredCompetences,
  getUnfulfilledRequirements,
} from "@medoma/models/activityRequirements";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { FlagsPopover } from "@/components/FlagsPopover/FlagsPopover";
import { KeyToPatientPopover } from "@/components/KeyToPatientPopover/KeyToPatientPopover";
import { PetPopover } from "@/components/PetPopover/PetPopover";
import { getFulfilledCompetenceRequirements } from "../planningViewUtils";
import { WarningIconPopover } from "@/components/Warnings/WarningIconPopover";
import { WarningsPopover } from "@/components/Warnings/WarningsPopover";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import TeamBadge from "@/components/Teams/TeamBadge";
import { TimeSpan } from "@/components/Time/TimeSpan";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import PinIcon from "@medoma/components/icons/PinIcon";
import {
  MedomaGridList,
  MedomaGridListItem,
} from "@/components/DragAndDrop/DragAndDrop";
import { DropZone } from "@/components/DragAndDrop/DropZone";
import { useDropToRoute } from "../DragAndDrop/useDropToRoute";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import { useReorderVisits } from "../DragAndDrop/useReorderVisits";
import { useIsRouteDropZoneAvailable } from "../DragAndDrop/useIsRouteDropZoneAvailable";
import { useDragAndDropFromRoute } from "../DragAndDrop/useDragAndDropFromRoute";
import { useSelection } from "../Selection/SelectionContext";
import {
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import { useShouldDisablePointerEventsForRouteDropZone } from "../DragAndDrop/useShouldDisablePointerEventsForRouteDropZone";
import { useDragAndDropContext } from "../DragAndDrop/DragAndDropContext";
import { useIsShiftDropZoneAvailable } from "../DragAndDrop/useIsShiftDropZoneAvailable";
import { useDropToShift } from "../DragAndDrop/useDropToShift";
import { getShiftDropZoneLabel } from "../DragAndDrop/dragAndDropUtils";
import { useIsShiftDropZoneEnlarged } from "../DragAndDrop/useIsShiftDropZoneEnlarged";
import { useDragAndDropFromShiftLane } from "../DragAndDrop/useDragAndDropFromShiftLane";
import { DragHandle } from "@/components/DragAndDrop/DragHandle";
import { calculateGroupStatus } from "../../Activities/ActivitiesTable/getDataFromActivityOccurrences";
import type { IRoute } from "@medoma/models/routes";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftWithContents,
} from "@medoma/models/shiftWithContents";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";

type ShiftWithRoutesAndActivitiesContextValue = {
  shiftId: number;
  shiftName: string;
  shiftStartDateTime: Date;
  shiftEndDateTime: Date;
  showArea: boolean;
  showTeam: boolean;
  showDuration: boolean;
  isExpanded: boolean;
  matchedItemIds: Set<string>;
};

const ShiftWithRoutesAndActivitiesContext =
  createContext<ShiftWithRoutesAndActivitiesContextValue | null>(null);

const useShiftWithRoutesAndActivitiesContext = () => {
  const value = useContext(ShiftWithRoutesAndActivitiesContext);
  if (value === null) {
    throw new Error(
      "useShiftWithRoutesAndActivitiesContext must be used within ShiftWithRoutesAndActivitiesContext",
    );
  }
  return value;
};

const getDurationInMinutes = (
  item: IVisit | INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
) =>
  isGroup(item)
    ? calculateGroupDuration(
        item.occurrences.map((occurrence) => occurrence.duration),
      )
    : item.duration;

const ShiftSectionHeader = ({
  shift,
  isDropZoneEnlarged,
}: {
  shift: IScheduledShift;
  isDropZoneEnlarged: boolean;
}) => {
  const { t } = useLingui();
  const shiftDisplayName = shiftName(shift, {
    competenceFormat: "full",
    employeeNameFormat: "full",
  });
  const { drag } = useDragAndDropContext();
  const { id: shiftId } = shift;
  const isShiftDropZoneAvailable = useIsShiftDropZoneAvailable({ shiftId });
  const { handleDrop } = useDropToShift(shiftId);

  return (
    <DropZone
      ariaLabel={getShiftDropZoneLabel(drag, shift)}
      onDrop={handleDrop}
      className={clsx(
        styles.fullColSpan,
        styles.shiftArticleHeader,
        styles.dropZone,
        styles.shiftDropZone,
        shift.deleted && styles.deleted,
        isShiftDropZoneAvailable &&
          drag &&
          (isDropZoneEnlarged ? enlargedDropZone.dropZone : styles.available),
      )}
      isDisabled={!isShiftDropZoneAvailable}
    >
      {shift.deleted ? (
        <div className={styles.deletedInfoText}>
          <Text element="span" size="small">
            <Trans>
              <strong>
                Detta arbetspass finns inte längre i schemaläggningssystemet.
              </strong>{" "}
              Flytta alla hembesök och övriga aktiviteter till ett annat
              arbetspass.
            </Trans>
          </Text>
        </div>
      ) : null}
      <div className={styles.shiftInfoRow}>
        <Heading level="h2" size="small">
          {shiftDisplayName}
        </Heading>
        {shift.team ? <TeamBadge team={shift.team} size="small" /> : null}
        <CompetenceChip
          competence={shift.competence}
          state="neutral"
          content={t(medicalCompetenceDictionary[shift.competence].short)}
          size="tiny"
        />
        <ShiftTime
          startDateTime={shift.startDateTime}
          endDateTime={shift.endDateTime}
          layout="horizontal"
          size="small"
        />
        {unusualShiftTypes[shift.type] ? (
          <>
            <div className={styles.separator} />
            <Text element="span" size="small" color="faded">
              {t(shiftTypesDictionary[shift.type])}
            </Text>
          </>
        ) : null}
      </div>
    </DropZone>
  );
};

const RouteHeader = ({ route }: { route: IRoute }) => {
  const { shiftId } = useShiftWithRoutesAndActivitiesContext();
  const { isRouteSelected, toggleRouteSelection } = useSelectionInTimeline();
  const routeSelected = isRouteSelected(route.id);
  return (
    <div className={clsx(styles.fullColSpan)}>
      <button
        className={clsx(
          styles.buttonReset,
          styles.routeHeader,
          styles.selectableItem,
          routeSelected && styles.selected,
        )}
        onClick={() => toggleRouteSelection(route, shiftId)}
      >
        <div className={styles.icon}>
          <PinIcon />
        </div>
        <Text element="div" size="smallest" color="faded" weight="medium">
          <Trans>Rutt</Trans>
        </Text>
      </button>
    </div>
  );
};

const PatientName = ({
  patient,
}: {
  patient: IListPatient | undefined | null;
}) => {
  if (!patient || !("name" in patient)) {
    return null;
  }

  const patientName = getPatientNameWithStatus(patient, "full");

  return (
    <div className={styles.patientNameAndIndicators}>
      <Text element="span" size="small" weight="medium">
        {patientName}
      </Text>
      <div className={styles.indicators}>
        {"flags" in patient && patient.flags ? (
          <FlagsPopover details={patient.flags} />
        ) : null}
        {"address" in patient && patient.address.keyToPatient ? (
          <KeyToPatientPopover details={patient.address.keyToPatient} />
        ) : null}
        {"pet" in patient && patient.pet ? (
          <PetPopover details={patient.pet} />
        ) : null}
      </div>
    </div>
  );
};

const VisitInRoute = ({
  visit,
  routeId,
}: {
  visit: IVisit;
  routeId: string;
}) => {
  const {
    shiftId,
    shiftStartDateTime,
    shiftEndDateTime,
    showArea,
    showTeam,
    showDuration,
    isExpanded,
    matchedItemIds,
  } = useShiftWithRoutesAndActivitiesContext();
  const { isVisitSelected, toggleVisitSelection } = useSelectionInTimeline();
  const { t } = useLingui();
  const isSelected = isVisitSelected(visit);
  const durationMinutes = getDurationInMinutes(visit);
  const visitRequiredCompetences = aggregateRequiredCompetences(
    visit.occurrences,
  );
  const unfulfilledRequirements = getUnfulfilledRequirements({
    assignees: visit.assignees,
    requiredCompetences: visitRequiredCompetences,
    doubleStaffing: visit.occurrences.some(
      (occurrence) => occurrence.doubleStaffing,
    ),
  });
  const hasDoubleStaffingUnfulfilled = unfulfilledRequirements.includes(
    activityRequirementSchema.enum.DoubleStaffing,
  );
  const otherUnfulfilledRequirements = unfulfilledRequirements.filter(
    (requirement) =>
      requirement !== activityRequirementSchema.enum.DoubleStaffing,
  );
  const fulfilledCompetenceRequirements = getFulfilledCompetenceRequirements({
    requiredCompetences: visitRequiredCompetences,
    unfulfilledRequirements,
  });
  const team = "team" in visit.patient ? visit.patient.team : undefined;
  const area = "area" in visit.patient ? visit.patient.area : undefined;
  const {
    start: visitStartTime,
    end: visitEndTime,
    isAnyTimeOfDay: visitIsAnyTimeOfDay,
  } = calculateVisitTimes({ occurrences: visit.occurrences });
  const visitHasStartAndEndTime = visitStartTime && visitEndTime;

  const patientLabel =
    visit.patient.status !== patientStatusSchema.enum.deleted
      ? getPatientFullName(visit.patient.name)
      : "";
  const timeSpanLabel = visitIsAnyTimeOfDay
    ? t(timeOfDayDictionary.Any.long)
    : visitStartTime && visitEndTime
      ? formattedTimeSpan(visitStartTime, visitEndTime)
      : "";
  const gridItemAriaLabel = [t`Hembesök`, patientLabel, timeSpanLabel]
    .filter(Boolean)
    .join(", ");

  const isUnfinished = visit.status !== visitStatusSchema.enum.finished;
  const warnings = [
    isUnfinished && otherUnfulfilledRequirements.length > 0
      ? t`Kompetensbehov uppfylls inte`
      : null,
    isUnfinished && hasDoubleStaffingUnfulfilled
      ? t`2 medarbetare krävs`
      : null,
    isUnfinished &&
    isVisitOutsideShiftSpan(visit, {
      startDateTime: shiftStartDateTime,
      endDateTime: shiftEndDateTime,
    })
      ? t`Besöket ligger utanför passets tid`
      : null,
  ].filter((warning) => warning !== null);

  return (
    <MedomaGridListItem
      id={visit.id}
      textValue={gridItemAriaLabel}
      onAction={() => {
        toggleVisitSelection(visit, routeId, shiftId);
      }}
      className={clsx(
        styles.fullColSpan,
        styles.subgrid,
        styles.visitOrActivity,
        styles.selectableItem,
        isSelected && styles.selected,
        visit.status === "finished" && styles.finishedItem,
        matchedItemIds.has(visit.id) && styles.matchHighlight,
      )}
    >
      <div className={styles.dragHandlePosition}>
        <DragHandle />
      </div>
      <div
        className={clsx(
          styles.categoryIcon,
          isExpanded && styles.categoryIconExpanded,
        )}
      >
        <CategoryIcon category={categorySchema.enum.HomeVisit} size="tiny" />
      </div>
      <div
        className={clsx(
          styles.patientNameAndActivityTitles,
          isExpanded && styles.expanded,
        )}
      >
        <PatientName patient={visit.patient} />
        {isExpanded ? (
          <ul className={styles.activityOccurrenceList}>
            {visit.occurrences.map((occurrence) => (
              <li key={occurrence.id}>
                <ActivityTitle
                  activityOccurrence={occurrence}
                  size="small"
                  weight="regular"
                  showDescriptionAndComment
                  showRecurringIcon
                />
              </li>
            ))}
          </ul>
        ) : (
          <ul className={styles.compactActivityOccurrenceList}>
            {visit.occurrences.map((occurrence) => (
              <li
                key={occurrence.id}
                className={styles.compactActivityOccurrenceItem}
              >
                <ActivityTitle
                  activityOccurrence={occurrence}
                  size="small"
                  weight="regular"
                  showDescriptionAndComment={false}
                  showRecurringIcon={false}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={styles.warnings}>
        {warnings.length > 0 ? <WarningsPopover warnings={warnings} /> : null}
      </div>
      <div className={styles.competenceChips}>
        {fulfilledCompetenceRequirements.map((competence) => (
          <CompetenceChip
            key={competence}
            competence={competence}
            state="neutral"
            size="tiny"
          />
        ))}
        {unfulfilledRequirements.length > 0
          ? unfulfilledRequirements.map((requirement, index) => (
              <RequirementChip
                key={`${requirement}-${index}`}
                requirement={requirement}
                state="unfulfilled"
                size="tiny"
              />
            ))
          : null}
      </div>
      {showArea ? (
        <div className={styles.area}>
          {area ? (
            <Text element="span" size="smallest" color="faded">
              {area.displayName}
            </Text>
          ) : null}
        </div>
      ) : null}
      {showTeam ? (
        <div className={styles.team}>
          {team ? <TeamBadge team={team} size="small" /> : null}
        </div>
      ) : null}
      {visitIsAnyTimeOfDay ? (
        <TimeSpan
          timespan={timeOfDaySchema.enum.Any}
          size="smallest"
          color="faded"
        />
      ) : visitHasStartAndEndTime ? (
        <TimeSpan
          timespan={{
            start: visitStartTime,
            end: visitEndTime,
          }}
          size="smallest"
          color="faded"
        />
      ) : (
        <Text element="span" size="smallest" color="faded">
          <Trans>Ogiltigt tidsfönster</Trans>
        </Text>
      )}
      {showDuration ? (
        <div>
          {durationMinutes > 0 ? (
            <DurationChip minutes={durationMinutes} />
          ) : null}
        </div>
      ) : null}
      <div className={styles.status}>
        <StatusTag
          status={visit.status}
          variant={
            getVisitStatusTimestamp(visit)
              ? "icon-with-timestamp"
              : "icon-and-text"
          }
          timestamp={getVisitStatusTimestamp(visit)}
          size="tiny"
        />
      </div>
    </MedomaGridListItem>
  );
};

const Route = ({ route }: { route: IRoute }) => {
  const { visits } = route;
  const { shiftId, shiftName } = useShiftWithRoutesAndActivitiesContext();
  const { t } = useLingui();
  const { handleDrop, isPending } = useDropToRoute(shiftId, route.id);
  const { reorder, isReordering } = useReorderVisits({ routeId: route.id });
  const isRouteDropZoneAvailable = useIsRouteDropZoneAvailable({
    routeStatus: route.status,
    routeId: route.id,
  });
  const { dragAndDropHooks } = useDragAndDropFromRoute({
    routeItem: route,
    reorder,
    shiftId,
  });
  const { selectedItem } = useSelection();

  const disablePointerEventsForRouteDropZone =
    useShouldDisablePointerEventsForRouteDropZone({ routeId: route.id });

  return (
    <LoadingOverlay
      key={route.id}
      show={isPending || isReordering}
      className={clsx(styles.fullColSpan, styles.subgrid)}
    >
      <DropZone
        className={clsx(
          styles.fullColSpan,
          styles.subgrid,
          styles.dropZone,
          isRouteDropZoneAvailable && styles.available,
        )}
        ariaLabel={t`Lägg till i rutt för ${shiftName}`}
        onDrop={handleDrop}
        isDisabled={isReordering || !isRouteDropZoneAvailable}
      >
        <RouteHeader route={route} />
        <MedomaGridList
          selectionMode="single"
          aria-label={t`Besök i rutt`}
          items={visits}
          dragAndDropHooks={dragAndDropHooks}
          dependencies={[selectedItem]}
          className={clsx(
            styles.fullColSpan,
            styles.subgrid,
            styles.visitsInRoute,
            disablePointerEventsForRouteDropZone &&
              styles.disablePointerEventsToNotInterfereWithRouteDropZone,
          )}
        >
          {(visit) => <VisitInRoute visit={visit} routeId={route.id} />}
        </MedomaGridList>
      </DropZone>
    </LoadingOverlay>
  );
};

const NonRoutableActivityOccurrenceOrGroup = ({
  nonRoutableActivityOccurrenceOrGroup,
}: {
  nonRoutableActivityOccurrenceOrGroup: INonRoutableEmployeePerformedActivityOccurrenceOrGroup;
}) => {
  const {
    shiftId,
    showArea,
    showTeam,
    showDuration,
    isExpanded,
    matchedItemIds,
  } = useShiftWithRoutesAndActivitiesContext();
  const {
    isActivityOccurrenceOrGroupSelected,
    toggleActivityOccurrenceSelection,
  } = useSelectionInTimeline();
  const { t } = useLingui();

  const isSelected = isActivityOccurrenceOrGroupSelected(
    nonRoutableActivityOccurrenceOrGroup,
  );
  const unfulfilledRequirements = getUnfulfilledRequirements({
    assignees: nonRoutableActivityOccurrenceOrGroup.assignees,
    requiredCompetences:
      nonRoutableActivityOccurrenceOrGroup.requiredCompetences,
  });
  const fulfilledCompetenceRequirements = getFulfilledCompetenceRequirements({
    requiredCompetences:
      nonRoutableActivityOccurrenceOrGroup.requiredCompetences,
    unfulfilledRequirements,
  });
  const team =
    nonRoutableActivityOccurrenceOrGroup.patient &&
    "team" in nonRoutableActivityOccurrenceOrGroup.patient
      ? nonRoutableActivityOccurrenceOrGroup.patient.team
      : undefined;
  const area =
    nonRoutableActivityOccurrenceOrGroup.patient &&
    "area" in nonRoutableActivityOccurrenceOrGroup.patient
      ? nonRoutableActivityOccurrenceOrGroup.patient.area
      : undefined;
  const { selectedItem } = useSelection();
  const { dragAndDropHooks } = useDragAndDropFromShiftLane({
    occurrenceOrGroupItem: nonRoutableActivityOccurrenceOrGroup,
    shiftId,
  });

  const categoryLabel = t(
    activityCategoryDictionary[nonRoutableActivityOccurrenceOrGroup.category],
  );
  const titleLabel = isGroup(nonRoutableActivityOccurrenceOrGroup)
    ? nonRoutableActivityOccurrenceOrGroup.occurrences
        .map((occurrence) => occurrence.title)
        .join(", ")
    : nonRoutableActivityOccurrenceOrGroup.title;
  const patientLabel =
    nonRoutableActivityOccurrenceOrGroup.patient &&
    "name" in nonRoutableActivityOccurrenceOrGroup.patient
      ? getPatientFullName(nonRoutableActivityOccurrenceOrGroup.patient.name)
      : "";
  const timeSpanLabel =
    nonRoutableActivityOccurrenceOrGroup.timeOfDay === timeOfDaySchema.enum.Any
      ? t(timeOfDayDictionary.Any.long)
      : formattedTimeSpan(
          nonRoutableActivityOccurrenceOrGroup.start,
          nonRoutableActivityOccurrenceOrGroup.end,
        );
  const status = isGroup(nonRoutableActivityOccurrenceOrGroup)
    ? calculateGroupStatus(nonRoutableActivityOccurrenceOrGroup)
    : nonRoutableActivityOccurrenceOrGroup.status;
  const statusTimestamp = isGroup(nonRoutableActivityOccurrenceOrGroup)
    ? getGroupStatusTimestamp(nonRoutableActivityOccurrenceOrGroup)
    : getActivityOccurrenceStatusTimestamp(
        nonRoutableActivityOccurrenceOrGroup,
      );
  const durationInMinutes = getDurationInMinutes(
    nonRoutableActivityOccurrenceOrGroup,
  );
  const gridItemAriaLabel = [
    categoryLabel,
    titleLabel,
    patientLabel,
    timeSpanLabel,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <MedomaGridList
      selectionMode="single"
      aria-label={t`Enskild aktivitet`}
      items={[nonRoutableActivityOccurrenceOrGroup]}
      dragAndDropHooks={dragAndDropHooks}
      dependencies={[selectedItem]}
      className={clsx(styles.fullColSpan, styles.subgrid)}
    >
      <MedomaGridListItem
        id={nonRoutableActivityOccurrenceOrGroup.id}
        textValue={gridItemAriaLabel}
        className={clsx(
          styles.fullColSpan,
          styles.subgrid,
          styles.visitOrActivity,
          styles.selectableItem,
          isSelected && styles.selected,
          status === "finished" && styles.finishedItem,
          matchedItemIds.has(nonRoutableActivityOccurrenceOrGroup.id) &&
            styles.matchHighlight,
        )}
        onAction={() =>
          toggleActivityOccurrenceSelection(
            nonRoutableActivityOccurrenceOrGroup,
            shiftId,
          )
        }
      >
        <div className={styles.dragHandlePosition}>
          <DragHandle />
        </div>
        <div
          className={clsx(
            styles.categoryIcon,
            isExpanded && styles.categoryIconExpanded,
          )}
        >
          <CategoryIcon
            category={nonRoutableActivityOccurrenceOrGroup.category}
            size="tiny"
          />
        </div>
        <div
          className={clsx(
            styles.patientNameAndActivityTitles,
            isExpanded && styles.expanded,
          )}
        >
          <PatientName patient={nonRoutableActivityOccurrenceOrGroup.patient} />
          {isExpanded ? (
            isGroup(nonRoutableActivityOccurrenceOrGroup) ? (
              <ul className={styles.activityOccurrenceList}>
                {nonRoutableActivityOccurrenceOrGroup.occurrences.map(
                  (occurrence) => (
                    <li key={occurrence.id}>
                      <ActivityTitle
                        activityOccurrence={occurrence}
                        size="small"
                        weight="regular"
                        showDescriptionAndComment
                        showRecurringIcon
                      />
                    </li>
                  ),
                )}
              </ul>
            ) : (
              <ActivityTitle
                activityOccurrence={nonRoutableActivityOccurrenceOrGroup}
                size="small"
                weight="regular"
                showDescriptionAndComment
                showRecurringIcon
              />
            )
          ) : isGroup(nonRoutableActivityOccurrenceOrGroup) ? (
            <ul className={styles.compactActivityOccurrenceList}>
              {nonRoutableActivityOccurrenceOrGroup.occurrences.map(
                (occurrence) => (
                  <li
                    key={occurrence.id}
                    className={styles.compactActivityOccurrenceItem}
                  >
                    <ActivityTitle
                      activityOccurrence={occurrence}
                      size="small"
                      weight="regular"
                      showDescriptionAndComment={false}
                      showRecurringIcon={false}
                    />
                  </li>
                ),
              )}
            </ul>
          ) : (
            <ActivityTitle
              activityOccurrence={nonRoutableActivityOccurrenceOrGroup}
              size="small"
              weight="regular"
              showDescriptionAndComment={false}
              showRecurringIcon={false}
            />
          )}
        </div>
        <div className={styles.warnings}>
          {unfulfilledRequirements.length > 0 && status !== "finished" ? (
            <WarningIconPopover ariaLabel={t`Kompetensbehov uppfylls inte`}>
              <Text element="p" size="small">
                <Trans>Kompetensbehov uppfylls inte</Trans>
              </Text>
            </WarningIconPopover>
          ) : null}
        </div>
        <div className={styles.competenceChips}>
          {fulfilledCompetenceRequirements.map((competence) => (
            <CompetenceChip
              key={competence}
              competence={competence}
              state="neutral"
              size="tiny"
            />
          ))}
          {unfulfilledRequirements.length > 0
            ? unfulfilledRequirements.map((requirement, index) => (
                <RequirementChip
                  key={`${requirement}-${index}`}
                  requirement={requirement}
                  state="unfulfilled"
                  size="tiny"
                />
              ))
            : null}
        </div>
        {showArea ? (
          <div className={styles.area}>
            {area ? (
              <Text element="span" size="smallest" color="faded">
                {area.displayName}
              </Text>
            ) : null}
          </div>
        ) : null}
        {showTeam ? (
          <div className={styles.team}>
            {team ? <TeamBadge team={team} size="small" /> : null}
          </div>
        ) : null}
        {nonRoutableActivityOccurrenceOrGroup.timeOfDay ===
        timeOfDaySchema.enum.Any ? (
          <TimeSpan
            timespan={timeOfDaySchema.enum.Any}
            size="smallest"
            color="faded"
          />
        ) : (
          <TimeSpan
            timespan={{
              start: nonRoutableActivityOccurrenceOrGroup.start,
              end: nonRoutableActivityOccurrenceOrGroup.end,
            }}
            size="smallest"
            color="faded"
          />
        )}
        {showDuration ? <DurationChip minutes={durationInMinutes} /> : null}
        <div className={styles.status}>
          <StatusTag
            status={status}
            variant={statusTimestamp ? "icon-with-timestamp" : "icon-and-text"}
            timestamp={statusTimestamp}
            size="tiny"
          />
        </div>
      </MedomaGridListItem>
    </MedomaGridList>
  );
};

const ShiftWithRoutesAndActivities = ({
  shiftWithContents,
  matchedItemIds,
  isExpanded,
}: {
  shiftWithContents: IShiftWithContents;
  matchedItemIds: Set<string>;
  isExpanded: boolean;
}) => {
  const { t } = useLingui();
  const { shift, items } = shiftWithContents;
  const { data: areas } = useAreas();
  const { data: teams } = useTeams();
  const { data: showDurationInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );
  const showArea = Boolean(areas && areas.length > 1);
  const showTeam = Boolean(teams && teams.length > 0);
  const showDuration = Boolean(showDurationInUI);

  const routeItems: IRoute[] = [];
  const nonRoutableActivityOccurrenceOrGroupItems: INonRoutableEmployeePerformedActivityOccurrenceOrGroup[] =
    [];
  for (const shiftItem of items.map(classifyShiftItem)) {
    switch (shiftItem.kind) {
      case shiftItemKindSchema.enum.route:
        routeItems.push(shiftItem.item);
        break;
      case shiftItemKindSchema.enum.group:
      case shiftItemKindSchema.enum.occurrence:
        nonRoutableActivityOccurrenceOrGroupItems.push(shiftItem.item);
        break;
      default:
        throw shiftItem satisfies never;
    }
  }
  const nonRoutableActivityOccurrencesOrGroupsWithPatient =
    nonRoutableActivityOccurrenceOrGroupItems.filter((item) =>
      Boolean(item.patient),
    );
  const nonRoutableActivityOccurrencesOrGroupsWithoutPatient =
    nonRoutableActivityOccurrenceOrGroupItems.filter((item) => !item.patient);

  const contextValue: ShiftWithRoutesAndActivitiesContextValue = {
    shiftId: shift.id,
    shiftName: shiftName(shift, {
      competenceFormat: "full",
      employeeNameFormat: "full",
    }),
    shiftStartDateTime: shift.startDateTime,
    shiftEndDateTime: shift.endDateTime,
    showArea,
    showTeam,
    showDuration,
    isExpanded,
    matchedItemIds,
  };

  const numberOfColumns =
    6 + (showArea ? 1 : 0) + (showTeam ? 1 : 0) + (showDuration ? 1 : 0);

  const mainGridClassName = clsx(
    styles.mainGrid,
    styles[`mainGridColumns${numberOfColumns}`],
  );

  const { isPending: isDroppingToShift } = useDropToShift(shift.id);
  const isDropZoneEnlarged = useIsShiftDropZoneEnlarged({
    shiftId: shift.id,
    hasRoutes: routeItems.length > 0,
  });

  return (
    <ShiftWithRoutesAndActivitiesContext.Provider value={contextValue}>
      <LoadingOverlay
        show={isDroppingToShift}
        className={clsx(styles.fullColSpan, styles.subgrid)}
      >
        <article
          className={clsx(
            styles.container,
            enlargedDropZone.shift,
            isDropZoneEnlarged && [
              styles.dropZoneEnlarged,
              enlargedDropZone.enlargedShift,
            ],
            mainGridClassName,
            styles.shiftWithRoutesAndActivities,
            shift.deleted && styles.deletedShift,
          )}
        >
          <ShiftSectionHeader
            shift={shift}
            isDropZoneEnlarged={isDropZoneEnlarged}
          />
          {items.length === 0 ? (
            <div
              className={clsx(styles.fullColSpan, styles.noRoutesOrActivities)}
            >
              <NoResults
                size="small"
                message={t`Inga besök eller aktiviteter planerade`}
              />
            </div>
          ) : (
            <div
              className={clsx(
                styles.fullColSpan,
                styles.subgrid,
                styles.routesAndActivities,
              )}
            >
              {routeItems.length > 0
                ? routeItems.map((route) => (
                    <section
                      key={route.id}
                      className={clsx(styles.fullColSpan, styles.subgrid)}
                    >
                      <Route route={route} />
                    </section>
                  ))
                : null}
              {nonRoutableActivityOccurrencesOrGroupsWithPatient.length > 0 ? (
                <section
                  className={clsx(
                    styles.fullColSpan,
                    styles.subgrid,
                    styles.nonRoutableActivityOccurrencesOrGroups,
                  )}
                >
                  {nonRoutableActivityOccurrencesOrGroupsWithPatient.map(
                    (nonRoutableActivityOccurrenceOrGroup) => (
                      <NonRoutableActivityOccurrenceOrGroup
                        nonRoutableActivityOccurrenceOrGroup={
                          nonRoutableActivityOccurrenceOrGroup
                        }
                        key={nonRoutableActivityOccurrenceOrGroup.id}
                      />
                    ),
                  )}
                </section>
              ) : null}
              {nonRoutableActivityOccurrencesOrGroupsWithoutPatient.length >
              0 ? (
                <section
                  className={clsx(
                    styles.fullColSpan,
                    styles.subgrid,
                    styles.nonRoutableActivityOccurrencesOrGroups,
                  )}
                >
                  {nonRoutableActivityOccurrencesOrGroupsWithoutPatient.map(
                    (nonRoutableActivityOccurrenceOrGroup) => (
                      <NonRoutableActivityOccurrenceOrGroup
                        nonRoutableActivityOccurrenceOrGroup={
                          nonRoutableActivityOccurrenceOrGroup
                        }
                        key={nonRoutableActivityOccurrenceOrGroup.id}
                      />
                    ),
                  )}
                </section>
              ) : null}
            </div>
          )}
        </article>
      </LoadingOverlay>
    </ShiftWithRoutesAndActivitiesContext.Provider>
  );
};

export { ShiftWithRoutesAndActivities };
